import moment from "moment";

import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { ValidationRule } from "@/Common/Api/Validation/Dto/Responses/ValidationRule";
import { ValidationRuleTypeEnum } from "@/Common/Api/Validation/Dto/Responses/ValidationRuleType";

class ValidationHelper {
    private RAMQCharMap = {};
    private maleLookupId = cacheStore.FindLookupByLookupCode("MALE");
    private femaleLookupId = cacheStore.FindLookupByLookupCode("FEMALE");

    public static emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
    public static urlRegex = /^((https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&\/\/=]*))$/i;

    constructor() {
        this.RAMQCharMap["0"] = 240;
        this.RAMQCharMap["1"] = 241;
        this.RAMQCharMap["2"] = 242;
        this.RAMQCharMap["3"] = 243;
        this.RAMQCharMap["4"] = 244;
        this.RAMQCharMap["5"] = 245;
        this.RAMQCharMap["6"] = 246;
        this.RAMQCharMap["7"] = 247;
        this.RAMQCharMap["8"] = 248;
        this.RAMQCharMap["9"] = 249;

        this.RAMQCharMap["A"] = 193;
        this.RAMQCharMap["B"] = 194;
        this.RAMQCharMap["C"] = 195;
        this.RAMQCharMap["D"] = 196;
        this.RAMQCharMap["E"] = 197;
        this.RAMQCharMap["F"] = 198;
        this.RAMQCharMap["G"] = 199;
        this.RAMQCharMap["H"] = 200;
        this.RAMQCharMap["I"] = 201;
        this.RAMQCharMap["J"] = 209;
        this.RAMQCharMap["K"] = 210;
        this.RAMQCharMap["L"] = 211;
        this.RAMQCharMap["M"] = 212;
        this.RAMQCharMap["N"] = 213;
        this.RAMQCharMap["O"] = 214;
        this.RAMQCharMap["P"] = 215;
        this.RAMQCharMap["Q"] = 216;
        this.RAMQCharMap["R"] = 217;
        this.RAMQCharMap["S"] = 226;
        this.RAMQCharMap["T"] = 227;
        this.RAMQCharMap["U"] = 228;
        this.RAMQCharMap["V"] = 229;
        this.RAMQCharMap["W"] = 230;
        this.RAMQCharMap["X"] = 231;
        this.RAMQCharMap["Y"] = 232;
        this.RAMQCharMap["Z"] = 233;
    }

    private getValidationRuleForCountryAndRegion(
        ruleType: ValidationRuleTypeEnum,
        countryLookupId: string,
        regionLookupId: string = null
    ): ValidationRule {
        const rulesForCountry = cacheStore.ValidationRules
            .filter(p => p.ValidationRuleTypeId === ruleType && p.CountryLookupId === countryLookupId);

        // get the region-agnostic rule for the country if region id is not supplied
        if (regionLookupId === null)
            return rulesForCountry.firstOrDefault(p => IsNullOrUndefined(p.RegionLookupId));

        // otherwise get the region-specific rule - falling back to the region-agnostic if none found
        return rulesForCountry.firstOrDefault(p => p.RegionLookupId === regionLookupId) ||
            rulesForCountry.firstOrDefault(p => IsNullOrUndefined(p.RegionLookupId));
    }

    public getHealthcardValidationRule(countryLookupId: string, regionLookupId: string = null): ValidationRule {
        return this.getValidationRuleForCountryAndRegion(
            ValidationRuleTypeEnum.HealthIdentification,
            countryLookupId,
            regionLookupId
        );
    }

    public getPostalCodeValidationRule(countryLookupId: string, regionLookupId: string = null): ValidationRule {
        return this.getValidationRuleForCountryAndRegion(
            ValidationRuleTypeEnum.PostalCode,
            countryLookupId,
            regionLookupId
        );
    }

    public getPhoneNumberValidationRule(countryLookupId: string, regionLookupId: string = null): ValidationRule {
        return this.getValidationRuleForCountryAndRegion(
            ValidationRuleTypeEnum.PhoneNumber,
            countryLookupId,
            regionLookupId
        );
    }

    public getSSNValidationRule(countryLookupId: string, regionLookupId: string = null): ValidationRule {
        return this.getValidationRuleForCountryAndRegion(
            ValidationRuleTypeEnum.SocialSecurityNumber,
            countryLookupId,
            regionLookupId
        );
    }

    public getFormattedPostalCodeString(value: string, countryLookupId: string, regionLookupId: string = null): string {
        const validationRule = this.getPostalCodeValidationRule(countryLookupId, regionLookupId);
        const displayFormatSelector: string = validationRule ? validationRule.DisplayFormatSelectorPatern : "";
        const displayFormatSubstitution: string = (validationRule) ? validationRule.DisplayFormatSubstitution : "";
        return this.getFormatedString(value, displayFormatSelector, displayFormatSubstitution);
    }

    public getFormattedPhoneNumberString(value: string, countryLookupId: string, regionLookupId: string = null): string {
        const validationRule = this.getPhoneNumberValidationRule(countryLookupId, regionLookupId);
        const displayFormatSelector: string = validationRule ? validationRule.DisplayFormatSelectorPatern : "";
        const displayFormatSubstitution: string = (validationRule) ? validationRule.DisplayFormatSubstitution : "";
        return this.getFormatedString(value, displayFormatSelector, displayFormatSubstitution);
    }

    public getEmailValidationRule(): ValidationRule {
        let rule: ValidationRule = new ValidationRule();
        rule.EditMaskPattern = ValidationHelper.GetEmailEditMaskPattern();
        rule.ValidationRegexPattern = " ^[\w -]+ (?:\.[\w -]+)*@(?:[\w -]+\.)+[a - zA - Z]{2, 7 } $";
        rule.ValidationErrorMessageLookupId = cacheStore.GetString("InvalidLabel");
        return rule;
    }

    public getFormatedString(str: string, selector: string, subst: string): string {
        if (typeof str === "undefined" || str === null || str.length === 0) {
            return "";
        }

        var result = str;

        if (selector && selector != "" && subst && subst != "") {
            var selRegEx = new RegExp(selector, "i");
            result = str.replace(selRegEx, subst);
        }

        return result;
    }

    public isFieldValid(fieldValue: string, rule: ValidationRule): boolean {
        var result = true;

        if (rule) {
            if (rule.ValidationRegexPattern && rule.ValidationRegexPattern.length > 0) {
                var regexp = new RegExp(rule.ValidationRegexPattern, "i");
                var result = ValidationHelper.isExactMatch(fieldValue, regexp);
            }
        }

        return result;
    }

    public validateNAM(val: string, namCountryLookupId: string, namRegionCountryId: string, patientFirstName: string, patientLastName: string, patientBirthdate: Date, patientGenderLookupId: string): boolean {
        let isValid = true;

        // allow an empty NAM to be valid
        if (IsNullOrWhiteSpace(val))
            return true;

        const validationRule = this.getHealthcardValidationRule(namCountryLookupId, namRegionCountryId);
        isValid = this.isFieldValid(val, validationRule);

        // extra QC NAM validation
        if (isValid == true && namRegionCountryId == cacheStore.FindLookupByLookupCode("CanadaState_QC").LookupID) {
            // check if nam matches birth date
            if (!(patientBirthdate == null || patientBirthdate.toString() == "")) {
                const momentDate = moment.utc(patientBirthdate);

                if (val.length >= 6) {
                    const yearLastDigits = val.substr(4, 2);
                    isValid = momentDate.format("YY") == yearLastDigits;
                }

                if (isValid && val.length >= 8) {
                    let monthsDigit = parseInt(val.substr(6, 2));
                    if (monthsDigit > 50) {
                        monthsDigit = monthsDigit - 50;
                        isValid = patientGenderLookupId != null ? patientGenderLookupId == this.femaleLookupId.LookupID : true; // validate selected gender with NAM
                    }
                    else
                        isValid = patientGenderLookupId != null ? patientGenderLookupId == this.maleLookupId.LookupID : true; // validate selected gender with NAM

                    isValid = isValid && momentDate.format("M") == monthsDigit.toString();
                }

                if (isValid && val.length >= 10) {
                    const days = val.substr(8, 2);
                    isValid = momentDate.format("DD") == days;
                }
            }

            // check if NAM matches Last name
            if (isValid == true && !IsNullOrWhiteSpace(patientLastName)) {
                let lastName = cacheStore.Helper.RemoveDiacritics(patientLastName);

                lastName = lastName.toUpperCase().replace(/[^a-zA-Z]/g, "");

                if (lastName.length < 3)
                    lastName = (lastName + "XXX").substring(0, 3); // Pad Right with X

                const lastNameKeepSpaces = cacheStore.Helper.RemoveDiacritics(patientLastName).toUpperCase().replace(/[^a-zA-Z]/g, " ");
                if (lastName.length >= 6 && lastNameKeepSpaces.substr(0, 6) == "SAINT ")
                    isValid = (val.substr(0, 3).toUpperCase() == "ST" + lastName.substr(5, 1));
                else if (lastName.length >= 7 && lastNameKeepSpaces.substr(0, 7) == "SAINTE ")
                    isValid = (val.substr(0, 3).toUpperCase() == "ST" + lastName.substr(6, 1));
                else if (lastName.length >= 4 && lastNameKeepSpaces.substr(0, 4) == "STE ")
                    isValid = (val.substr(0, 3).toUpperCase() == "ST" + lastName.substr(3, 1));
                else
                    isValid = (val.substr(0, 3).toUpperCase() == lastName.substr(0, 3));
            }

            // check if NAM matches First name
            if (isValid == true && !IsNullOrWhiteSpace(patientFirstName)) {
                let firstName = cacheStore.Helper.RemoveDiacritics(patientFirstName);
                firstName = firstName.toUpperCase().replace(/[^a-zA-Z]/g, "");

                isValid = (firstName.substring(0, 1).toUpperCase() == val.substr(3, 1));
            }

            // Validate LastDigit
            if (isValid == true)
                isValid = this.validateQuebecRAMQLastDigit(val, patientBirthdate);
        }

        return isValid;
    }

    public isOrderedNAMValid(OrderedNAM: string, ValidationDigit: number): boolean {
        var multiplicators = [1, 3, 7, 9, 1, 7, 1, 3, 4, 5, 7, 6, 9, 1];

        var i = 0;
        var namValue = 0;
        for (var j = 0; j < OrderedNAM.length; j++) {
            var char: string = OrderedNAM.charAt(j);
            namValue += this.RAMQCharMap[char] * multiplicators[i];
            i++;
        }
        var endValue = namValue % 10;

        return (endValue == ValidationDigit);
    }

    public static validateUrl(value: string): boolean {
        if (!value || value.length == 0)
            return true;

        return ValidationHelper.isExactMatch(value, this.urlRegex);
    }

    public static formatPhoneNumberCanadaUS(number: string): string {
        var result = "";
        if (!IsNullOrWhiteSpace(number)) {
            var indicative = number.substring(0, 3);
            var part1 = number.substring(3, 6);
            var part2 = number.replace(indicative, "").replace(part1, "");
            result = "(" + indicative + ")" + " " + part1 + " " + part2;
        }
        return result;
    }

    public static isExactMatch(str: string, regex: RegExp): boolean {
        if (str == null) return false;

        var match = str.match(regex);

        return match != null && str == match[0];
    }

    public static GetEmailEditMaskPattern(): string {
        return "*{1,64}[.*{1,64}][.*{1,64}][.*{1,64}][.*{1,64}][.*{1,64}][.*{1,64}][.*{1,64}][.*{1,64}][.*{1,64}][.*{1,63}]@-{1,63}.-{1,63}[.-{1,63}][.-{1,63}][.-{1,63}][.-{1,63}][.-{1,63}]";
    }

    /**
     * Removes any non-numeric from phone number string
     * If countryLookupID is not null, tries to remove the phone country code for CA US and MX
     * @param number
     * @param countryLookupId
     */
    public static cleanPhoneNumber(number: string, countryLookupId: string): string {
        var result: string = null;

        if (number != null) {
            result = number.replace(/\D/g, "");
        }

        if (countryLookupId != null) {
            let country = cacheStore.GetLookupById(countryLookupId);
            if (country.LookupCode == "CNCANADA" || country.LookupCode == "CNUSA") {
                // check if first char is country code (1) - remove it
                if (result != null && result.length > 10 && result[0] == "1") {
                    result = result.substr(1, result.length - 1);
                }
            }
            else if (country.LookupCode == "CNMEXICO") {
                // check if first 2 char is country code (52) - remove it if so
                if (result != null && result.length > 10 && result.substr(0, 2) == "52") {
                    result = result.substr(2, result.length - 2);
                }
            }
        }

        return result;
    }

    public static validatePhoneNumber(phone: PhoneNumber): PhoneNumber {
        const validationHelper = new ValidationHelper();
        const validationRule = validationHelper.getPhoneNumberValidationRule(phone.CountryLookupId, null); // no need for region when checking for phone number format?
        const validationRegex = validationRule?.ValidationRegexPattern ?? "";
        phone.IsValid = true;

        if (phone.Number.length == 0)
            phone.IsValid = false;

        if (validationRegex?.length > 0) {
            const regexp = new RegExp(validationRegex);
            const valid = ValidationHelper.isExactMatch(phone.Number, regexp);
            phone.IsValid = valid;
        }

        return phone;
    }

    public static validatePostalCode(postalCode: string, countryLookupId: string): boolean {
        const validationHelper = new ValidationHelper();
        const validationRule = validationHelper.getPostalCodeValidationRule(countryLookupId, null); // no need for region when checking for postal code format?
        const validationRegex: string = validationRule ? validationRule.ValidationRegexPattern : "";

        if (validationRegex && validationRegex.length > 0) {
            const regexp = new RegExp(validationRegex, "i");
            return ValidationHelper.isExactMatch(postalCode, regexp);
        }

        return true;
    }

    private validateQuebecRAMQLastDigit(nam: string, patientDob?: Date): boolean {
        var year = nam.substr(4, 2);

        var monthstr = nam.substr(6, 2);
        var month = parseInt(nam.substr(6, 2));

        var sex = "M";
        if (month > 50) {
            sex = "F";
            month -= 50;

            if (month.toString().length == 1)
                monthstr = "0" + month.toString();
            else
                monthstr = month.toString();
        }

        let validationValue = parseInt(nam[11].toString());

        if (patientDob) {
            let dob = moment(patientDob);
            let orderredNAM = nam.substr(0, 4) + dob.format("YYYY") + sex + dob.format("MM") + nam.substr(8, 3);
            return this.isOrderedNAMValid(orderredNAM, validationValue);
        }

        var reOrderedNAM1 = nam.substr(0, 4) + "19" + year + sex + monthstr + nam.substr(8, 3); // NOMIAAAASxMMJJSV
        var reOrderedNAM2 = nam.substr(0, 4) + "20" + year + sex + monthstr + nam.substr(8, 3); // NOMIAAAASxMMJJSV

        if (this.isOrderedNAMValid(reOrderedNAM1, validationValue) == true)
            return true;
        else if (this.isOrderedNAMValid(reOrderedNAM2, validationValue) == true)
            return true;

        return false;
    }

    /**
     * Validates the search date range based on the provided 'from' and 'to' dates.
     * It checks the following rules:
     * - Both dates are required.
     * - The 'from' date cannot be greater than the 'to' date.
     * - The difference between the 'from' and 'to' dates must be less than 5 years, with the 'to' date being adjusted by subtracting one day to enforce the rule.
     * If any of these conditions are violated, appropriate error messages are returned.
     *
     * @param fromDate The starting date for the search range.
     * @param toDate The ending date for the search range.
     * @returns A string containing the error message if any validation fails; otherwise, null if the date range is valid.
    */
    public static validateSearchDateRangeMax5Years(fromDate: Date, toDate: Date): string | null {
        if (!!fromDate && !!toDate && (fromDate > toDate))
            return cacheStore.GetString("StartDateCannotBeGreaterThanEndDateLabel");

        if (!fromDate && !toDate)
            return cacheStore.GetString("StartAndEndDatesAreRequiredLabel");
        else if (!fromDate)
            return cacheStore.GetString("StartDateIsRequiredLabel");
        else if (!toDate)
            return cacheStore.GetString("EndDateIsRequiredLabel");

        // we subtract a day from the `to date` to  keep the range of exactly 5 years valid.
        if (moment(moment(toDate).subtract(1, "days")).diff(moment(fromDate), "years") >= 5)
            return cacheStore.GetString("SearchMedicalDocumentDateRangeErrorLabel");

        return null;
    }
}

export { ValidationHelper };
